<template>
  <CContainer>
    <div
      class="sr-only"
      role="status"
      aria-live="polite"
      aria-atomic="true"
      data-test-id="teacher-pass-live-status"
    >
      {{ state.liveStatusMessage }}
    </div>
    <CRow
      v-if="!state.showTodaysHistory"
      data-test-id="create-teacher-pass-form"
      class="justify-content-center"
    >
      <CCol md="12" class="text-start px-0">
        <CCol class="mt-4">
          <Label class="v3teacher-pass-label" required> Student </Label>
          <LazyLoadSelect
            data-test-id="teacher-pass-select-student"
            :is-multiple="false"
            :selected="state.form.user_id"
            :invalid-feedback="errors.user_id"
            type="student"
            placeholder="Search students"
            @changed="
              (value) => {
                state.form.user_id = value
              }
            "
          >
          </LazyLoadSelect>
          <InfoBox v-if="state.serverErrors.user_id" class="danger mt-2">
            {{ state.serverErrors.user_id[0] }}
          </InfoBox>
          <InfoBox
            v-if="
              state.form.user_id &&
              state.userInfo &&
              state.userInfo.polarity_active
            "
            class="danger mt-2"
          >
            The hall is currently busy for this student. Please see your
            building admin for any questions.
          </InfoBox>
          <InfoBox
            v-if="
              state.form.user_id &&
              state.userInfo &&
              state.userInfo.show_active_pass_limit_message
            "
            class="danger mt-2"
          >
            Active Pass Limit has been reached. By clicking create, you will
            exceed the limit.
          </InfoBox>

          <CCol v-if="state.form.user_id" class="mt-2 v3teacher-pass-today-box">
            <div>
              Today's Passes:
              <button
                type="button"
                class="box cursor-pointer ms-1 border-0"
                :aria-label="`Open today's pass history. ${
                  (state.userInfo && state.userInfo.user_passes_count) || 0
                } passes.`"
                @click="showPassesModal"
              >
                {{ state.userInfo && state.userInfo.user_passes_count }}
                <i class="ri-door-open-line" />
              </button>
              <span v-if="state.userInfo && state.userInfo.passes_remain">
                The student has {{ state.userInfo.passes_remain }} pass(es)
                remaining today.
              </span>
            </div>
          </CCol>
        </CCol>
        <CCol class="mt-2">
          <Label
            label-id="teacher-pass-destination-legend"
            class="v3teacher-pass-label"
            required
          >
            Where do you want to send the student?
          </Label>
          <div
            role="group"
            aria-labelledby="teacher-pass-destination-legend"
            class="teacher-pass-destination-group"
          >
            <FavoriteCarousel
              class="mb-3"
              :key="'destination'"
              :role="currentUser.role.name"
              :current-user="currentUser"
              section="destination"
              :is-editable="
                (currentUser.role.name == 'teacher' ||
                  currentUser.role.name == 'staff') ??
                false
              "
              classes="pb-0 pt-0 mt-2 smaller-size"
              :action="(elem) => getCapacityReached(elem)"
              :selected="state.form.to_id"
            />
            <LazyLoadSelect
              data-test-id="teacher-pass-select-destination"
              input-id="teacher-pass-destination-combo"
              labelled-by="teacher-pass-destination-legend"
              hide-floating-label
              accessible-label="Where do you want to send the student"
              :is-multiple="false"
              :selected="state.form.to_id"
              :invalid-feedback="errors.to_id"
              placeholder="To:"
              type="combined"
              :adult-pass-request-disabled="true"
              :force-clear-cache="true"
              @changed="(elem) => getCapacityReached(elem)"
            >
            </LazyLoadSelect>
            <InfoBox class="danger mt-2" v-if="state.serverErrors.to_id">
              {{ state.serverErrors.to_id[0] }}
            </InfoBox>

            <InfoBox class="danger mt-2" v-if="state.serverErrors.to_type">
              {{ state.serverErrors.to_type[0] }}
            </InfoBox>
            <InfoBox
              v-if="
                state.form.to_id &&
                state.form.to_id.value &&
                state.form.to_id.value.unavailability
              "
              class="danger mt-2"
            >
              This adult is unavailable until
              <span
                class="mx-1"
                v-html="
                  $helpers.getHTMLDateTime(
                    state.form.to_id.value.unavailability.to_date,
                    'MMMM D, YYYY',
                    'h:mm A'
                  ) + '.'
                "
              />
              <span v-if="state.form.to_id.value.unavailability.comment"
                >Note: {{ state.form.to_id.value.unavailability.comment }}</span
              ></InfoBox
            >
            <InfoBox v-if="isCurrRoomRestricted" class="danger mt-2">
              This location is restricted for this student. By clicking create
              you will override this restriction.
            </InfoBox>
            <InfoBox v-if="toCapacityReached" class="danger mt-2">
              Maximum capacity for the location has been reached. By clicking
              create you will exceed the max.
            </InfoBox>
          </div>
        </CCol>
        <CCol class="mt-2">
          <Label class="v3teacher-pass-label" for="teacher-pass-input-reason">
            Reason
          </Label>
          <InputField
            id="teacher-pass-input-reason"
            data-test-id="teacher-pass-input-reason"
            v-model="state.form.comment"
            max-chars="250"
            :rows="4"
            class="w-full"
            type="textarea"
          />
        </CCol>
        <CCol class="mt-2 d-flex align-items-center v3teacher-pass-switch-wrap">
          <CustomSwitch
            v-model="state.form.auto_approve"
            test-id="teacher-pass-auto-approve-switch"
            id="teacher-pass-auto-approve-switch"
          />
          <label
            for="teacher-pass-auto-approve-switch"
            class="ms-2 v3teacher-pass-label"
          >
            Pre-approve pass
            <span class="v3teacher-pass-switch-state ms-1">
              ({{ state.form.auto_approve ? "On" : "Off" }})
            </span>
          </label>
        </CCol>
        <CCol class="mt-2">
          <CCol class="mt-2">
            <div v-if="isUserAllowTardy" class="d-flex flex-column mt-2">
              <div class="mb-1 v3teacher-pass-label">Mark this pass as:</div>
              <TardyRadioGroup v-model="state.form.tardy_badge" />
            </div>
          </CCol>

          <CCol class="mt-2">
            <InfoBox
              data-test-id="teacher-pass-info-box"
              v-if="state.serverRes"
              class="danger mt-4"
              >{{ state.serverRes.message }}</InfoBox
            ></CCol
          >

          <div v-if="!isInView">
            <div
              class="d-flex align-items-center justify-content-center mt-4 mb-1 text-center"
            >
              <LoadingButton class="mx-2" @click="closeModal" rounded
                >Cancel</LoadingButton
              >
              <LoadingButton
                @click="submit()"
                :is-loading="state.isLoading"
                solid
                rounded
                class="me-2"
              >
                Create
              </LoadingButton>
              <router-link
                class="text-graident-blue"
                to="/teacher-pass"
                @click="closeModal()"
              >
                <div class="d-flex align-items-center">
                  <img
                    width="18"
                    class="m-0 me-2"
                    src="@/assets/images/icons/gradient-link.png"
                  />
                  <span class="text-graident-blue">Go to full menu</span>
                </div>
              </router-link>
            </div>
          </div>
          <div v-else class="mt-4">
            <LoadingButton
              data-test-id="teacher-pass-button-create"
              @click="submit()"
              :is-loading="state.isLoading"
              solid
              rounded
              class="me-3 px-4"
            >
              Create
            </LoadingButton>
          </div>
        </CCol>
      </CCol>
    </CRow>

    <div v-if="state.showTodaysHistory && state.form && state.form.user_id">
      <hr class="mt-2" />
      <CRow class="m-2">
        <CCol class="d-flex justify-content-start align-items-center cursor">
          <LoadingButton
            class="align-items-center pe-3"
            @click="showPassesModal"
            solid
            rounded
          >
            <i class="ri-arrow-left-line me-1 font-sm" />Back
          </LoadingButton>
        </CCol>
        <CCol class="d-flex justify-content-center mt-2">
          <h4>Today's History</h4>
        </CCol>
        <CCol class="d-flex justify-content-end align-items-center">
          <HelpCenterButton content-key="todays_history"> </HelpCenterButton>
        </CCol>
      </CRow>

      <StudentPassHistory
        data-test-id="teacher-pass-todays-history"
        :set-data="true"
        :user-id="state.form.user_id.value.id"
        :is-in-modal="true"
      />
    </div>
  </CContainer>
</template>

<script>
import {
  reactive,
  computed,
  onMounted,
  onBeforeUnmount,
  inject,
  watch
} from "vue"
import { useStore } from "vuex"
import { helpers, required, requiredIf } from "@vuelidate/validators"
import { useRouter } from "vue-router"
import useVuelidate from "@vuelidate/core"
import StudentPassHistory from "@/v3components/Forms/Students/StudentPassHistory.vue"
import TardyRadioGroup from "@/v3components/Forms/TardyRadioGroup.vue"
import LazyLoadSelect from "@/v3components/shared/Form/LazyLoadSelect.vue"
import FavoriteCarousel from "../FavoriteCarousel.vue"
import Label from "@/v3components/shared/Form/Label.vue"
import InputField from "@/v3components/shared/Form/InputField.vue"
import CustomSwitch from "@/v3components/shared/Form/CustomSwitch.vue"
import LoadingButton from "@/v3components/shared/Buttons/LoadingButton.vue"
import InfoBox from "@/v3components/shared/Alerts/InfoBox.vue"
import HelpCenterButton from "@/v3components/HelpCenterButton.vue"

export default {
  name: "CreateTeacherPassForm",
  components: {
    TardyRadioGroup,
    LazyLoadSelect,
    FavoriteCarousel,
    Label,
    InputField,
    CustomSwitch,
    LoadingButton,
    InfoBox,
    StudentPassHistory,
    HelpCenterButton
  },
  props: ["isInView"],
  emits: ["closeModal", "cancel"],
  setup(props, { emit }) {
    const store = useStore()
    const modal = inject("modal")
    const route = useRouter()

    let submitSuccessTimerId = null

    const state = reactive({
      isLoading: false,
      liveStatusMessage: "",
      form: {
        user_id: null,
        to_id: [],
        comment: "",
        auto_approve: true,
        tardy_badge: ""
      },
      serverErrors: {},
      serverRes: null,
      modal: {
        isVisible: {
          todayPasses: false
        }
      },
      userInfo: null,
      polarityMessage: null,
      isCapacityReached: false,
      showTodaysHistory: false
    })

    const validationMessages = {
      required: "This field is required"
    }

    const validations = {
      form: {
        user_id: {
          required: helpers.withMessage(validationMessages.required, required)
        },
        to_id: {
          required: helpers.withMessage(validationMessages.required, required),
          isRestricted: helpers.withMessage(
            validationMessages.required,
            requiredIf(() => {
              if (
                state.form.to_id &&
                state.form.to_id.value &&
                !state.form.to_id.value.isRestricted
              ) {
                return true
              }
              return false
            })
          )
        },
        auto_approve: {}
      }
    }
    const v$ = useVuelidate(validations.form, state.form)

    const isValid = computed(() => !v$.value.$invalid)

    const errors = computed(() => {
      const errorObj = {}
      v$.value.$errors.forEach((err) => {
        errorObj[err.$property] = err.$message
      })

      return errorObj
    })

    onMounted(() => {
      if (currentUser.value.role == "admin") {
        store.dispatch("polarities/getPolarityMessage").then((res) => {
          if (res && res.data && res.data.data) {
            state.polarityMessage = res.data.data.message
              ? res.data.data.message
              : res.data.data.initial_default_value
          }
        })
      }
    })

    onBeforeUnmount(() => {
      if (submitSuccessTimerId != null) {
        clearTimeout(submitSuccessTimerId)
        submitSuccessTimerId = null
      }
    })

    const currentUser = computed(() => store.getters["authentication/user"])

    const tardyManagementOptions = computed(
      () => store.getters["schools/tardyManagementOptions"]
    )

    const toCapacityReached = computed(() => {
      return state.form && state.form.to_id && state.isCapacityReached
    })

    const isCurrRoomRestricted = computed(() => {
      return (
        state.userInfo &&
        state.form.to_id &&
        state.form.to_id.value &&
        state.form.to_id.value.id &&
        state.userInfo.restricted_rooms &&
        state.userInfo.restricted_rooms.includes(state.form.to_id.value.id)
      )
    })

    const isZeroPassRemainingForStudent = computed(() => {
      if (!state?.userInfo?.passes_remain) {
        return false
      }
      const remainingPassCount = state.userInfo.passes_remain.match(/\d+/)
      if (!remainingPassCount || remainingPassCount.length === 0) {
        return false
      }
      const passCount = Number(remainingPassCount[0])
      if (isNaN(passCount)) {
        return false
      }
      return passCount <= 0
    })

    const isLimitExceeded = computed(() => {
      const isStudentPassLimitExceeded =
        isZeroPassRemainingForStudent?.value || false
      const isPolarityActive =
        (state?.form?.user_id && state?.userInfo?.polarity_active) || false
      const isActivePassLimitReached =
        (state?.form?.user_id &&
          state?.userInfo?.show_active_pass_limit_message) ||
        false
      const isRoomRestricted = isCurrRoomRestricted?.value || false
      const isCapacityReached = toCapacityReached?.value || false
      return (
        isStudentPassLimitExceeded ||
        isPolarityActive ||
        isActivePassLimitReached ||
        isRoomRestricted ||
        isCapacityReached
      )
    })

    const activeDashboardSettings = computed(() => {
      let current = localStorage.getItem("dashSettings")
      if (current) {
        current = JSON.parse(current)
        current.activePassState = "only_active"
        return false
      }
      return current
    })

    const isUserAllowTardy = computed(() => {
      if (tardyManagementOptions.value != null) {
        if (tardyManagementOptions.value.usage["within-ehp"]) {
          if (currentUser.value.role.name == "admin") {
            return true
          } else if (
            (currentUser.value.role.name == "staff" ||
              currentUser.value.role.name == "teacher") &&
            tardyManagementOptions.value["allow-teacher-staff"]
          ) {
            return true
          } else if (
            currentUser.value.allow_tardy &&
            tardyManagementOptions.value["allow-selected-users-only"]
          ) {
            return true
          }
          return false
        }
        return false
      }
      return false
    })

    const getCapacityReached = (value) => {
      state.form.to_id = value
      state.isCapacityReached = false
      if (value.value.type === "App\\Models\\Room") {
        store
          .dispatch("rooms/getCapacityReached", value.value.id)
          .then((response) => {
            if (response.data) {
              const data = response.data.data
              //if limit is set to "-" the room is unlimited
              state.isCapacityReached =
                data.capacity_reached && data.limit != "-"
            }
          })
      }
      store
        .dispatch("rooms/getUnavailability", {
          id: value.value.id,
          type: value.value.type
        })
        .then((response) => {
          if (response.data.data) {
            state.form.to_id.value.unavailability = response.data.data
          }
        })
    }

    const submit = () => {
      setResponseInfoBox()
      if (isValid.value) {
        state.liveStatusMessage = "Creating teacher pass."
        const pass = {
          user_id: state.form.user_id.value.id,
          to_id: state.form.to_id.id
            ? state.form.to_id.id
            : state.form.to_id.value.id,
          to_type: state.form.to_id.type
            ? state.form.to_id.type
            : state.form.to_id.value.type,
          comment: state.form.comment,
          auto_approve: state.form.auto_approve,
          tardy_badge: state.form.tardy_badge,
          limit_exceeded: isLimitExceeded.value ? 1 : 0
        }
        state.isLoading = true
        store
          .dispatch("proxyPasses/createProxyPass", pass)
          .then(() => {
            state.liveStatusMessage =
              "Teacher pass created. Redirecting to dashboard."
            submitSuccessTimerId = setTimeout(() => {
              submitSuccessTimerId = null
              state.serverErrors = {}
              try {
                if (typeof localStorage !== "undefined") {
                  localStorage.removeItem("dashSettings")
                }
              } catch {
                /* storage may be unavailable (e.g. private mode / tests) */
              }
              state.isLoading = false
              closeModal()
              route.push("/dashboard")
            }, 500)
          })
          .catch((err) => {
            const response = err?.response?.data
            if (!response) {
              state.serverErrors = {}
              setResponseInfoBox(
                "Error",
                "An unexpected error occurred. Please try again."
              )
              state.liveStatusMessage =
                "An unexpected error occurred. Please try again."
              state.isLoading = false
              return
            }
            state.serverErrors = response.errors ? response.errors : {}
            setResponseInfoBox("Error", response.message)
            state.liveStatusMessage = response.message || "Error creating pass."
            state.isLoading = false
          })
      } else {
        state.liveStatusMessage =
          "Please complete the required fields before creating a pass."
        v$.value.$touch()
      }
    }

    const resetForm = () => {
      state.form = {
        user_id: null,
        to_id: [],
        comment: "",
        auto_approve: true,
        tardy_badge: ""
      }
    }

    const setResponseInfoBox = (title, message) => {
      if (title || message) {
        state.serverRes = {
          message,
          title
        }
      } else {
        state.serverRes = null
      }
    }

    const showPassesModal = () => {
      if (props.isInView) {
        state.liveStatusMessage = "Opening today's pass history."
        modal.open(StudentPassHistory, {
          size: "lg",
          title: "Today's History",
          props: {
            setData: true,
            userId: state.form.user_id.value.id
          },
          help: "todays_history"
        })
      } else {
        state.showTodaysHistory = !state.showTodaysHistory
        state.liveStatusMessage = state.showTodaysHistory
          ? "Showing today's pass history."
          : "Showing teacher pass form."
      }
    }

    const closeModal = () => {
      resetForm()
      setResponseInfoBox()
      emit("closeModal")
      emit("cancel")
    }

    watch(
      () => state.form.user_id,
      (val) => {
        if (val && val.value) {
          state.userInfo = null
          store
            .dispatch("proxyPasses/getProxyPassInfo", val.value.id)
            .then((res) => {
              state.userInfo = res
            })
        }
      }
    )

    return {
      store,
      state,
      errors,
      currentUser,
      tardyManagementOptions,
      toCapacityReached,
      isCurrRoomRestricted,
      activeDashboardSettings,
      isUserAllowTardy,
      getCapacityReached,
      submit,
      showPassesModal,
      closeModal,
      resetForm,
      props,
      setResponseInfoBox
    }
  }
}
</script>
